import dayjs from "dayjs";
import type { LocationQueryRaw } from "vue-router";
import { DATE_FORMAT, TIME_FORMAT } from "@lib/constants/system";
import { useMaintenanceStore } from "@/store/maintenance";
import { useSystemBannersStore } from "@/store/systemBanners";
import type { CancelablePromise, TSystemStatusEventsResponse } from "@/interfaces/api";

let unwatch: ReturnType<typeof watchEffect> | undefined;
let cancel: CancelablePromise<TSystemStatusEventsResponse> | undefined;

export default defineNuxtRouteMiddleware(async (to) => {
  const { $i18n } = useNuxtApp();
  const $router = useRouter();
  const store = useMaintenanceStore();
  const bannerStore = useSystemBannersStore();
  const { activeMaintenanceEvent, upcomingMaintenanceNotShownEvent } = storeToRefs(store);

  if (unwatch) {
    unwatch();
    unwatch = undefined;
  }

  if (cancel) {
    cancel.cancel();
    cancel = undefined;
  }

  if (to.fullPath.startsWith("/developer") || to.fullPath.startsWith("/embed")) {
    return;
  }

  unwatch = watchEffect(() => {
    const event = upcomingMaintenanceNotShownEvent.value
      ? {
          ...upcomingMaintenanceNotShownEvent.value,
        }
      : null;

    if (event) {
      const startDate = dayjs(event?.planned_start).toDate();
      const endDate = dayjs(event?.planned_end).toDate();
      bannerStore.setBanner(
        $i18n.t("maintenance-banner.message", {
          plannedStartDate: formatDateTime(startDate, DATE_FORMAT),
          plannedStartTime: formatDateTime(startDate, TIME_FORMAT),
          plannedEndDate: formatDateTime(endDate, DATE_FORMAT),
          plannedEndTime: formatDateTime(endDate, TIME_FORMAT),
        }),
        "info",
        () => {
          if (event?.id) {
            store.setEventShownToday(event.id);
          }
        },
      );
    }

    // Return to prev page from maintenance
    if (!activeMaintenanceEvent.value && to.path.startsWith("/maintenance")) {
      let url = "/";
      if ("next" in to.query) {
        url = to.query.next as string;
      }
      $router.replace(url);
      return;
    }
    if (activeMaintenanceEvent.value && !to.path.startsWith("/maintenance")) {
      const query = {} as LocationQueryRaw;
      if (to.path !== "/") {
        query.next = to.path;
      }
      $router.replace({ path: "/maintenance", query });
    }
  });

  cancel = store.fetchEvents();
});
